import React, { useEffect, useMemo, useState } from "react";
import {
  SortableContext,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { CSS } from "@dnd-kit/utilities";
import {
  PlusIcon,
  TableCellsIcon,
  ExclamationTriangleIcon,
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useReportBuilderStore } from "./store";
import { ColumnCard } from "./ColumnCard";
import { DATA_TYPE_CONFIG } from "./constants";
import { ReportColumn, ReportGrain } from "./types";
import { previewCustomReport } from "../../../api/customReportApi";
import { validateReportFormulas } from "./FormulaEditor";

// ─── Grain-to-incompatible-table map (mirrors backend) ───────────────────────

const GRAIN_EXCLUDED: Record<ReportGrain, string[]> = {
  grant: [],
  employee: ["vesting", "transaction"],
  plan: ["vesting", "transaction", "grant"],
  vesting_tranche: ["transaction"],
  transaction: ["vesting"],
};

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyCanvas({ simple }: { simple: boolean }) {
  if (simple) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <div className="w-12 h-12 rounded-full bg-activeColor flex items-center justify-center">
          <TableCellsIcon className="w-6 h-6 text-inputBorderFocus" />
        </div>
        <p className="text-sm font-semibold text-gray-700">No columns yet</p>
        <p className="text-xs text-gray-400 max-w-xs">
          Click the <strong>+</strong> button next to any column in the panel on
          the left to add it here, or drag a column from the schema browser.
        </p>
      </div>
    );
  }
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 py-16 px-8 text-center">
      <div className="w-16 h-16 rounded-full bg-activeColor flex items-center justify-center">
        <TableCellsIcon className="w-8 h-8 text-inputBorderFocus" />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-1">
          No columns added yet
        </h3>
        <p className="text-xs text-gray-400 max-w-xs leading-relaxed">
          Pick columns from the left panel to get started, or drag them directly
          onto the canvas.
        </p>
      </div>
      <div className="flex items-center gap-2 text-[11px] text-gray-400 bg-gray-50 border border-dashed border-gray-200 rounded-lg px-4 py-2">
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M7 16V4m0 0L3 8m4-4 4 4M17 8v12m0 0 4-4m-4 4-4-4"
          />
        </svg>
        Drag columns here from the schema browser
      </div>
    </div>
  );
}

// ─── Drop zone indicator ──────────────────────────────────────────────────────

function DropZoneIndicator({ isOver }: { isOver: boolean }) {
  return (
    <div
      className={`flex-shrink-0 w-24 h-full min-h-[120px] border-2 border-dashed rounded-xl flex items-center justify-center transition-colors ${
        isOver ? "border-primary bg-activeColor" : "border-gray-200 bg-gray-50"
      }`}
    >
      <PlusIcon
        className={`w-5 h-5 ${isOver ? "text-primary" : "text-gray-300"}`}
      />
    </div>
  );
}

// ─── Simple mode: sortable row chip ──────────────────────────────────────────

export function SimpleColumnRow({ col }: { col: ReportColumn }) {
  const { removeColumn, setActiveColumn, activeColumnId } =
    useReportBuilderStore();
  const cfg = DATA_TYPE_CONFIG[col.type] ?? DATA_TYPE_CONFIG.string;
  const isActive = activeColumnId === col.id;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: col.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => setActiveColumn(isActive ? null : col.id)}
      className={`group flex items-center gap-3 px-3 py-2.5 bg-white border rounded-lg cursor-pointer select-none transition-all ${
        isActive
          ? "border-primary shadow-sm"
          : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
      }`}
    >
      <button
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        className="cursor-grab text-gray-300 hover:text-gray-400 flex-shrink-0"
      >
        <Bars3Icon className="w-4 h-4" />
      </button>
      <span className="w-5 text-center text-[10px] font-semibold text-gray-400 flex-shrink-0">
        #
      </span>
      <span className="flex-1 text-sm font-medium text-gray-800 truncate">
        {col.displayName}
      </span>
      <span
        className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border flex-shrink-0 ${cfg.bg} ${cfg.text} ${cfg.border}`}
      >
        {col.isCustom ? "ƒ" : cfg.label}
      </span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          removeColumn(col.id);
        }}
        className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-gray-300 hover:text-red-400"
        title="Remove"
      >
        <XMarkIcon className="w-4 h-4" />
      </button>
    </div>
  );
}

function LivePreview({
  rows,
  columnDefs,
  warnings,
  error,
  isLoading,
}: {
  rows: Record<string, unknown>[];
  columnDefs: ColDef[];
  warnings: string[];
  error: string | null;
  isLoading: boolean;
}) {
  return (
    <section
      data-tour="report-preview"
      aria-label="Live preview"
      className="flex h-72 shrink-0 flex-col overflow-hidden border-t border-borderColor bg-white"
    >
      <div className="flex items-center gap-2 border-b border-borderColor px-4 py-2">
        <TableCellsIcon className="h-4 w-4 text-secondaryText" />
        <span className="text-xs font-medium text-primaryText">
          Live preview
        </span>
        <span className="text-xs text-secondaryText">
          {isLoading
            ? "Updating…"
            : `${rows.length} sample record${
                rows.length !== 1 ? "s" : ""
              } shown (up to 10)`}
        </span>
      </div>
      {error ? (
        <div className="px-4 py-6 text-xs text-amber-700 bg-amber-50">
          {error}
        </div>
      ) : (
        <>
          {warnings.length > 0 && (
            <div className="border-b border-amber-100 bg-amber-50 px-4 py-2 text-xs text-amber-700">
              {warnings.join(" ")}
            </div>
          )}
          <div className="ag-theme-alpine min-h-0 flex-1">
            <AgGridReact
              rowData={rows}
              columnDefs={columnDefs}
              defaultColDef={{ sortable: true, resizable: true }}
              suppressCellFocus
              suppressMovableColumns
              animateRows={false}
              headerHeight={34}
              rowHeight={34}
              overlayNoRowsTemplate="No preview records found"
            />
          </div>
        </>
      )}
    </section>
  );
}

// ─── Report Canvas ────────────────────────────────────────────────────────────

interface ReportCanvasProps {
  isDragOver: boolean;
}

export function ReportCanvas({ isDragOver }: ReportCanvasProps) {
  const { config, activeSheetId, addCustomColumn, isAdvancedMode } =
    useReportBuilderStore();

  const activeSheet =
    config.sheets.find((s) => s.id === activeSheetId) ?? config.sheets[0];
  const { setNodeRef: setSimpleDropRef, isOver: isSimpleDropOver } =
    useDroppable({ id: "simple-report-canvas" });
  const [previewRows, setPreviewRows] = useState<Record<string, unknown>[]>([]);
  const [previewWarnings, setPreviewWarnings] = useState<string[]>([]);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const grainWarnings = useMemo(() => {
    const excluded = GRAIN_EXCLUDED[activeSheet.reportGrain] ?? [];
    if (excluded.length === 0) return [];
    return activeSheet.columns
      .filter((c) => c.sourceTable && excluded.includes(c.sourceTable))
      .map((c) => c.displayName);
  }, [activeSheet.columns, activeSheet.reportGrain]);

  const columnIds = activeSheet.columns.map((c) => c.id);
  const allColumnIds = new Set(columnIds);

  const previewColumns = activeSheet.columns;
  const previewColDefs = useMemo<ColDef[]>(
    () =>
      previewColumns.map((column) => ({
        field: column.id,
        headerName: column.displayName,
        minWidth: 150,
        flex: 1,
        sortable: true,
        resizable: true,
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined) return "—";
          if (column.type === "percentage") {
            const value = Number(params.value);
            return Number.isFinite(value)
              ? `${(value * 100).toFixed(1)}%`
              : "—";
          }
          return String(params.value);
        },
      })),
    [previewColumns]
  );
  const formulaError = useMemo(() => validateReportFormulas(config), [config]);

  useEffect(() => {
    let cancelled = false;
    if (previewColumns.length === 0) {
      setPreviewRows([]);
      setPreviewWarnings([]);
      setPreviewError(null);
      setIsPreviewLoading(false);
      return () => {
        cancelled = true;
      };
    }
    if (formulaError) {
      setPreviewRows([]);
      setPreviewWarnings([]);
      setPreviewError(formulaError);
      return () => {
        cancelled = true;
      };
    }

    setIsPreviewLoading(true);
    setPreviewError(null);
    const timer = window.setTimeout(async () => {
      try {
        const result = await previewCustomReport(config);
        const sheet = result.sheets.find((item) => item.id === activeSheet.id);
        if (!cancelled) {
          setPreviewRows(sheet?.rows ?? []);
          setPreviewWarnings(sheet?.warnings ?? []);
        }
      } catch {
        if (!cancelled) {
          setPreviewRows([]);
          setPreviewWarnings([]);
          setPreviewError("Could not load a live preview. Please try again.");
        }
      } finally {
        if (!cancelled) setIsPreviewLoading(false);
      }
    }, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [config, activeSheet.id, formulaError, previewColumns.length]);

  const warningBanner = grainWarnings.length > 0 && (
    <div className="flex items-start gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200">
      <ExclamationTriangleIcon className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
      <p className="text-xs text-amber-700">
        <span className="font-semibold">
          {isAdvancedMode
            ? `Incompatible columns for ${activeSheet.reportGrain.replace(
                "_",
                " "
              )} basis: `
            : `Some columns are not available on ${activeSheet.reportGrain.replace(
                "_",
                " "
              )} basis: `}
        </span>
        {grainWarnings.join(", ")} &mdash; they will be excluded from the
        export.
      </p>
    </div>
  );

  // ── Simple mode ───────────────────────────────────────────────────────────

  if (!isAdvancedMode) {
    return (
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {warningBanner}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <div className="px-4 py-2.5 bg-white border-b border-gray-100 flex items-center gap-2">
            <TableCellsIcon className="w-4 h-4 text-gray-400" />
            <span className="text-xs font-medium text-gray-600">
              {activeSheet.columns.length === 0
                ? "No columns selected"
                : `${activeSheet.columns.length} column${
                    activeSheet.columns.length !== 1 ? "s" : ""
                  } selected`}
            </span>
          </div>
          <div
            ref={setSimpleDropRef}
            className={`flex-1 overflow-y-auto px-4 py-3 transition-colors ${
              isDragOver || isSimpleDropOver ? "bg-activeColor" : "bg-gray-50"
            }`}
          >
            {activeSheet.columns.length === 0 ? (
              <EmptyCanvas simple />
            ) : (
              <SortableContext
                items={columnIds}
                strategy={verticalListSortingStrategy}
              >
                <div className="flex flex-col gap-1.5 max-w-lg">
                  {activeSheet.columns.map((col) => (
                    <SimpleColumnRow key={col.id} col={col} />
                  ))}
                </div>
              </SortableContext>
            )}
          </div>
          {activeSheet.columns.length > 0 && (
            <LivePreview
              rows={previewRows}
              columnDefs={previewColDefs}
              warnings={previewWarnings}
              error={previewError}
              isLoading={isPreviewLoading}
            />
          )}
        </div>
      </div>
    );
  }

  // ── Advanced mode ─────────────────────────────────────────────────────────

  return (
    <div className="flex-1 flex flex-col min-w-0 bg-gray-50 overflow-hidden">
      {warningBanner}

      {/* Canvas toolbar */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 font-medium">
            {activeSheet.columns.length} column
            {activeSheet.columns.length !== 1 ? "s" : ""}
          </span>
          {activeSheet.columns.length > 0 && (
            <span className="text-[10px] text-gray-400">· Live preview</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={addCustomColumn}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
          >
            <span className="font-bold text-sm leading-none">ƒ</span>
            Custom column
          </button>
        </div>
      </div>

      {/* Column cards */}
      <div className="flex-shrink-0 overflow-x-auto px-4 py-4">
        {activeSheet.columns.length === 0 ? (
          <EmptyCanvas simple={false} />
        ) : (
          <SortableContext
            items={columnIds}
            strategy={horizontalListSortingStrategy}
          >
            <div className="flex items-start gap-3 pb-2">
              {activeSheet.columns.map((col) => (
                <div key={col.id} className="w-44 flex-shrink-0">
                  <ColumnCard col={col} allColumnIds={allColumnIds} />
                </div>
              ))}
              <DropZoneIndicator isOver={isDragOver} />
            </div>
          </SortableContext>
        )}
      </div>

      {/* Preview table */}
      {activeSheet.columns.length > 0 && (
        <LivePreview
          rows={previewRows}
          columnDefs={previewColDefs}
          warnings={previewWarnings}
          error={previewError}
          isLoading={isPreviewLoading}
        />
      )}
    </div>
  );
}
